import { Alert } from "@volca/ui/components/ui/alert";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { extractErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import { EmailForwardingInstructions } from "@/onboarding/simple-onboarding/email-forwarding-instructions";
import { OnboardingInformationList } from "@/onboarding/simple-onboarding/onboarding-information-list";
import { useReopenOnboarding } from "@/onboarding/simple-onboarding/shared-query";
import { EMAIL_INVOICE_SOURCES } from "@/onboarding/simple-onboarding/types";
import type { SubmittedScreenProps } from "./types";

export const SubmittedScreen = ({ business, state }: SubmittedScreenProps) => {
  const reopen = useReopenOnboarding(business.id);
  const byEmail = state.answers.invoice_sources?.some((source) =>
    EMAIL_INVOICE_SOURCES.includes(source),
  );
  return (
    <Stack
      gap={4}
      align="center"
      className="min-h-page w-full bg-surface-sunken px-4 py-8"
    >
      <Stack gap={6} align="stretch" className="w-full max-w-3xl">
        <Stack className="w-full" align="start" gap={2}>
          <PageTitle>
            {state.submitted_at
              ? "Your information is with Volca"
              : "Waiting for setup"}
          </PageTitle>
          <Text tone="muted">
            We will review your information and complete your setup. We will
            contact you if we need anything else.
          </Text>
          {state.submitted_at && (
            <Text variant="caption" tone="muted">
              Submitted {dayjs(state.submitted_at).format("MMMM D, YYYY")}
            </Text>
          )}
        </Stack>
        {state.submitted_at && (
          <Stack className="w-full" gap={4} align="stretch">
            <Stack className="w-full" align="start" gap={1}>
              <Text variant="title">Your information</Text>
              <Text tone="muted">
                Need to make a correction? Select Edit to return to that step.
                Your saved information stays in place. Submit it again when you
                are finished.
              </Text>
            </Stack>
            {reopen.isError && (
              <Alert color={"danger"}>
                {extractErrorMessage(reopen.error).message}
              </Alert>
            )}
            <OnboardingInformationList
              state={state}
              companyName={business.name}
              timeZone={business.time_zone_label}
              pending={reopen.isPending}
              onEdit={(step) => reopen.mutate(step)}
            />
          </Stack>
        )}
        {byEmail && (
          <details className="rounded-xl border border-line bg-surface-raised">
            <summary className="focus-visible:outline-auto cursor-pointer px-5 py-4 text-sm font-semibold">
              Read email forwarding instructions
            </summary>
            <Stack gap={4} className="w-full px-5 pb-5" align="stretch">
              <EmailForwardingInstructions
                embedded
                address={state.ap_inbound_email}
                check={state.forwarding_check}
              />
            </Stack>
          </details>
        )}
      </Stack>
    </Stack>
  );
};
